import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import stylex from '@stylexjs/stylex';
import { PreloaderCircular } from '@shared/ui/kit';
import { useGetSegmentsByDepartments, EmployeeDepartment } from '@entities/event/lib/hooks/useFetchSegmentTree';
import { Ticket, TicketsFilterInput } from '@pages/event/api/useFetchTicketSegment';
import { ISegmentParticipants } from '@features/add-participants-modal';

import { SegmentStructureTree } from '../segment-structure-tree';
import { styles } from './styles';
import { pruneDepartment } from './ui/filterTree';

interface SegmentsTreeProps {
  ticketFilter?: TicketsFilterInput;
  toggleUser?: (ticket: Ticket) => void;
  checkedUsers?: { [ticketId: number]: number };
  setCheckedUsers?: React.Dispatch<React.SetStateAction<{ [ticketId: number]: number }>>;
  segment: ISegmentParticipants;
  search: string;
  registerRefetch?: (segmentId: number, refetchFn: () => void) => void;
  onDelete?: (ticket: Ticket) => void;
  onGetQr?: (ticketId: number) => void;
  eventId?: number;
  refetchSegments?: () => void;
  refetchCategories?: () => void;
  eventDateEnd?: string;
  eventDateStart?: string;
}

const SegmentsTreeComponent: React.FC<SegmentsTreeProps> = ({
  toggleUser,
  checkedUsers,
  setCheckedUsers,
  segment,
  search,
  registerRefetch,
  onDelete,
  onGetQr,
  eventId,
  refetchSegments,
  refetchCategories,
  eventDateEnd,
  eventDateStart,
  ticketFilter,
}) => {
  const { id } = useParams();
  const eventIdFromParams = Number(id);
  const finalEventId = eventId ?? eventIdFromParams;

  const { data, fetching, error, refetch } = useGetSegmentsByDepartments(finalEventId, ticketFilter);
  useEffect(() => {
    registerRefetch?.(segment.id, () => refetch({ requestPolicy: 'network-only' }));
  }, [registerRefetch, segment.id, refetch]);

  const q = (search ?? '').toLowerCase().trim();

  const segmentDepartments =
    data?.getSegmentsByDepartments
      ?.find((s) => s.id === segment.id)
      ?.departments.filter(({ name }) => name !== 'Внештатные пользователи') ?? [];

  const visibleDepartments = useMemo(
    () => (segmentDepartments ?? []).map((dep) => pruneDepartment(dep, q)).filter(Boolean) as EmployeeDepartment[],
    [segmentDepartments, q]
  );

  if (fetching) {
    return (
      <div {...stylex.props(styles.overlay)}>
        <PreloaderCircular size="large" />
      </div>
    );
  }
  if (error) return <div>Ошибка загрузки данных</div>;

  return (
    <div {...stylex.props(styles.container)}>
      <div {...stylex.props(styles.main)}>
        {visibleDepartments.map((department) => (
          <SegmentStructureTree
            key={department.id}
            department={department}
            toggleUser={toggleUser}
            checkedUsers={checkedUsers}
            setCheckedUsers={setCheckedUsers}
            search={search}
            onDelete={onDelete}
            onGetQr={onGetQr}
            eventId={eventId}
            refetchSegments={refetchSegments}
            refetchCategories={refetchCategories}
            eventDateEnd={eventDateEnd}
            eventDateStart={eventDateStart}
          />
        ))}
      </div>
    </div>
  );
};

export const SegmentsTree = React.memo(SegmentsTreeComponent);
